import { useAuthStore } from '@/auth/auth-store'

/**
 * Nucleus assistant panel integration.
 *
 * The panel is injected by a third-party loader script (see `index.html`), so it
 * lives outside React's tree and cannot use `useNavigate`. It signals intent
 * instead: when the agent cites a record, it emits a `navigate` event carrying an
 * entity type and id, and the host decides what URL that means. This keeps the
 * URL scheme ours — Nucleus never hardcodes console routes.
 *
 * The entity types handled here must match the tenant's Navigation config in the
 * Nucleus admin portal (Settings → Navigation). Today that is:
 *   farmer    → /farmers/{id}
 *   executive → /executives/{id}
 */

/** Entity reference emitted by the panel. Shape is fixed by the Nucleus contract. */
interface NucleusEntityRef {
  readonly type: string
  readonly id: string
  readonly label?: string
}

interface NucleusApi {
  on: (event: 'navigate', handler: (entity: NucleusEntityRef) => void) => void
  identify: (args: { jwt: string } | null) => void
  reset: () => void
}

declare global {
  interface Window {
    nucleus?: NucleusApi
  }
}

/**
 * Route for an entity, or null when we have no page for that type.
 *
 * Returning null rather than guessing matters: the panel's entity types come from
 * tenant config that can be edited in the Nucleus admin portal without any console
 * change, so this console can legitimately receive a type it has no route for. A
 * guessed URL would land the user on a 404 with no explanation.
 */
export function routeForEntity(entity: NucleusEntityRef): string | null {
  if (!entity.id) return null
  switch (entity.type) {
    case 'farmer':
      return `/farmers/${encodeURIComponent(entity.id)}`
    case 'executive':
      return `/executives/${encodeURIComponent(entity.id)}`
    case 'validator':
      // No validator DETAIL route exists yet — the console only has the list
      // page. Deep-link to the list pre-filtered by the validator's display
      // name (the page maps ?search= to the API's q param). Uses the label,
      // not the id, because the backend search matches names/phones, not ids.
      // Falls back to the unfiltered list when the chip has no label.
      return entity.label ? `/validators?search=${encodeURIComponent(entity.label)}` : '/validators'
    default:
      return null
  }
}

/**
 * Wire the panel's navigate event to the console router.
 *
 * Safe to call when the loader has not run — if `window.nucleus` is absent (script
 * blocked, offline, or the embed removed) this is a no-op and the console behaves
 * exactly as it does today. The panel is an enhancement, never a dependency.
 */
/**
 * Just the slice of the router this needs. Typed structurally rather than against
 * `@remix-run/router` (a transitive dep we should not import directly) so the
 * integration stays testable with a plain stub.
 */
export interface NavigableRouter {
  navigate: (to: string) => void | Promise<void>
}

export function registerNucleusNavigation(router: NavigableRouter): void {
  const api = window.nucleus
  if (!api) {
    console.warn('[nucleus] window.nucleus absent — panel not loaded, navigation not wired')
    return
  }

  api.on('navigate', (entity) => {
    const path = routeForEntity(entity)
    if (!path) {
      // Deliberately a warning, not a thrown error or a toast: an unmapped type is
      // a configuration mismatch between Nucleus and this console, not something
      // the person reading a chat answer can act on.
      console.warn('[nucleus] no console route for entity type', entity.type)
      return
    }
    void router.navigate(path)
  })
}

/**
 * The slice of the auth store this needs: the current token and a way to be told
 * when it changes. Typed structurally rather than against the concrete Zustand
 * store so the wiring stays testable with a plain stub, exactly as
 * `registerNucleusNavigation` is tested against a stub router.
 */
export interface AuthTokenSource {
  getState: () => { token: string | null }
  subscribe: (
    listener: (state: { token: string | null }, prev: { token: string | null }) => void,
  ) => () => void
}

/**
 * Forward the console user's identity to the panel so the agent acts as that user.
 *
 * The panel starts anonymous. Calling `identify({ jwt })` makes the gateway use the
 * user's own token as the tool-call bearer, so the Varri backend enforces that
 * user's validator scope natively and the panel only offers role-appropriate tools.
 * On logout (token → null) we `identify(null)`, returning the panel to its anonymous
 * read-only session — a signed-out user's JWT is never left live in the panel. The
 * latest token wins, so a re-login (or a refreshed token) updates the next turn.
 *
 * Safe when the loader never ran: if `window.nucleus` is absent this is a no-op,
 * matching `registerNucleusNavigation` — the panel is an enhancement, not a
 * dependency. The JWT is only handed to the panel in memory; this wiring never
 * persists or logs it (the auth store already owns its own persistence).
 */
export function registerNucleusIdentity(auth: AuthTokenSource = useAuthStore): void {
  const api = window.nucleus
  if (!api) {
    console.warn('[nucleus] window.nucleus absent — panel not loaded, identity not wired')
    return
  }

  // Seed the panel with the current session, if any. No token ⇒ leave it anonymous
  // (don't call identify(null) redundantly — anonymous is the panel's default).
  const current = auth.getState().token
  if (current) api.identify({ jwt: current })

  // Keep the panel's identity in lockstep with the console session.
  auth.subscribe((state, prev) => {
    if (state.token === prev.token) return
    if (state.token) api.identify({ jwt: state.token })
    else api.identify(null)
  })
}

/**
 * Inject the Nucleus loader and wire navigation, if this deployment is configured
 * for it.
 *
 * Deliberately code-driven rather than a `<script>` tag in `index.html`: the panel
 * host, tenant slug and gateway URL differ per environment, and a hardcoded tag
 * would either pin localhost into production or need build-time HTML rewriting.
 * Env-gated means an unconfigured deployment ships no panel and no dead script tag.
 *
 * Required env (all three, or nothing loads):
 *   VITE_NUCLEUS_PANEL_URL   e.g. http://localhost:5174
 *   VITE_NUCLEUS_TENANT      e.g. varri
 *   VITE_NUCLEUS_GATEWAY     e.g. http://localhost:3000
 */
/**
 * Whether the assistant may load at all in this build.
 *
 * Production is now an intended target: per-user identity forwarding and native
 * validator-scope enforcement shipped, so the agent answers each user only within
 * their own permissions. The two real gates remain — the deployment must be
 * explicitly configured (`VITE_NUCLEUS_*` present, checked in `mountNucleusPanel`)
 * AND the load must opt in (`?agent=true`). A build with no Nucleus env still ships
 * no panel, in every environment. (Was previously hard-blocked in production while
 * the agent was under staging-only evaluation.)
 */
function assistantAllowedHere(): boolean {
  return true
}

/**
 * Whether the assistant should mount on this page load.
 *
 * Default ON — the agent is out of evaluation and is now the standard console
 * experience for every logged-in user. `?agent=false` opts out for a single
 * load (a debug/escape hatch); any other value, or no parameter at all, mounts
 * it. Checked once at startup.
 */
function assistantRequested(): boolean {
  if (typeof window === 'undefined') return false
  return new URLSearchParams(window.location.search).get('agent') !== 'false'
}

export function mountNucleusPanel(router: NavigableRouter): void {
  if (!assistantAllowedHere()) return
  if (!assistantRequested()) return

  const panelUrl = import.meta.env.VITE_NUCLEUS_PANEL_URL
  const tenant = import.meta.env.VITE_NUCLEUS_TENANT
  const gateway = import.meta.env.VITE_NUCLEUS_GATEWAY
  if (!panelUrl || !tenant || !gateway) return

  const script = document.createElement('script')
  script.src = `${panelUrl.replace(/\/$/, '')}/loader.js`
  script.defer = true
  script.dataset.tenant = tenant
  script.dataset.gateway = gateway
  // The loader defines window.nucleus as it initialises, so the handlers can only be
  // attached once the script has run. On failure the console is simply panel-less.
  script.addEventListener('load', () => {
    registerNucleusNavigation(router)
    registerNucleusIdentity()
  })
  script.addEventListener('error', () =>
    console.warn('[nucleus] loader failed to load from', script.src),
  )
  document.body.appendChild(script)
}
